import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Leaf, CheckCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }

    pollPaymentStatus(sessionId);
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempt >= maxAttempts) {
      setStatus("error");
      toast.error("Payment verification timed out. Please check your email for confirmation.");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/subscriptions/status/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.payment_status === "paid") {
        setStatus("success");
        // Refresh user data to get updated subscription
        await checkAuth();
        toast.success("Subscription activated! Welcome to your new plan.");
        return;
      } else if (response.data.status === "expired") {
        setStatus("error");
        toast.error("Payment session expired. Please try again.");
        return;
      }

      // Continue polling
      setAttempts(attempt + 1);
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
    } catch (error) {
      console.error("Status check error:", error);
      setAttempts(attempt + 1);
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="card-organic max-w-md w-full">
        <CardContent className="p-8 text-center">
          <Link to="/" className="flex items-center justify-center gap-2 mb-8">
            <Leaf className="w-8 h-8 text-primary" />
            <span className="font-serif text-xl font-bold text-primary">ROOTSPACE</span>
          </Link>

          {status === "checking" && (
            <>
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
              <h1 className="font-serif text-2xl font-bold mb-2">Verifying Payment</h1>
              <p className="text-muted-foreground mb-6">
                Please wait while we confirm your subscription...
              </p>
              <p className="text-sm text-muted-foreground">
                Attempt {attempts + 1} of 5
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
              <h1 className="font-serif text-2xl font-bold mb-2">Welcome Aboard!</h1>
              <p className="text-muted-foreground mb-6">
                Your subscription is now active. Time to grow something amazing!
              </p>
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={() => navigate('/create')}
                  className="btn-primary w-full"
                  data-testid="create-garden-btn"
                >
                  Create Your First Garden
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/profile')}
                  data-testid="view-profile-btn"
                >
                  View Your Profile
                </Button>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">!</span>
              </div>
              <h1 className="font-serif text-2xl font-bold mb-2">Something Went Wrong</h1>
              <p className="text-muted-foreground mb-6">
                We couldn&apos;t verify your payment. If you were charged, please contact support.
              </p>
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={() => navigate('/pricing')}
                  className="btn-primary w-full"
                >
                  Try Again
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/')}
                >
                  Go Home
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
